import { type ReactNode } from 'react';
import { type Platform, PlatformContext } from './PlatformContext';
import { standardAPIs, testAPIs } from '../core/api/index.ts';
import { env } from '../core/util/env.ts';
import { loadDevOverrides } from '../core/util/devOverrides';
import { realNavigation } from './navigation/realNavigation';
import { createRealAppOpener } from './app-opener/realAppOpener';
import { realIframe } from './iframe/realIframe';
import { type IframeAdapter } from './iframe/IframeAdapter';
import { realDevice } from './device/realDevice';
import { type DeviceAdapter } from './device/DeviceAdapter';

// Dev menu can force device/embedding traits (persisted in localStorage, applied on reload).
// Gated to non-prod so a prod build never reads the override store.
const applyDevPlatformOverrides = (
  device: DeviceAdapter,
  iframe: IframeAdapter
): { device: DeviceAdapter; iframe: IframeAdapter } => {
  if (env.PROD) {
    return { device, iframe };
  }
  const { device: d, iframe: f } = loadDevOverrides();
  return {
    device: d ? { ...device, ...d } : device,
    iframe:
      f ?
        {
          ...iframe,
          isEmbedded: f.isEmbedded === undefined ? iframe.isEmbedded : () => f.isEmbedded!,
          isWidgetSdkEmbed:
            f.isWidgetSdkEmbed === undefined ? iframe.isWidgetSdkEmbed : () => f.isWidgetSdkEmbed!,
        }
      : iframe,
  };
};

export const PlatformProvider = ({
  children,
  overrides,
}: {
  children: ReactNode;
  overrides?: Partial<Platform>;
}) => {
  const navigation = overrides?.navigation ?? realNavigation;
  const { device, iframe } = applyDevPlatformOverrides(realDevice, realIframe);
  const value: Platform = {
    apis: env.DEV ? testAPIs : standardAPIs,
    navigation,
    appOpener: createRealAppOpener(navigation),
    iframe,
    device,
    ...overrides,
  };

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
};
